import { useId } from 'react'
import { Field, FieldGrid, TextInput, CodeSelect, toLocalInput, fromLocalInput } from './fields'
import { EntityPicker } from '@/shared/pickers/EntityPicker'
import type { IncidentVocabulary } from '../api'

/**
 * Step 5 · Attribution and provenance — who, allegedly, and where the claim came from.
 *
 * ## Everything here is an ALLEGATION until verification says otherwise
 *
 * Naming a perpetrator, a party or a candidate in an election-incident record is the
 * highest-consequence thing this form does. The step says so in the panel below rather
 * than leaving the reporter to infer it, and the whole step is skippable: an unattributed
 * report of something that happened is far more useful than an attributed guess.
 *
 * The party and candidate fields are SEARCHABLE PICKERS over the coalitions-parties-and-
 * candidates module, not id boxes. They were id boxes, and a hand-typed uuid gives three
 * bad outcomes: a typo that fails a foreign key at save, a valid uuid pointing at the
 * wrong record, and an allegation of election violence attached to whichever party that
 * string happens to name. Picking from a list shows the name the reader will see.
 */
export function StepAttribution({
  vocab, value, onChange, errors,
}: {
  vocab: IncidentVocabulary | null
  value: {
    perpetrator: string | null
    perpetratorPartyId: string | null
    relatedCandidateId: string | null
    relatedPartyId: string | null
    sourceOutlet: string | null
    sourceUrl: string | null
    sourcePublishedAt: string | null
    mediaCoverageCode: string | null
  }
  onChange: (patch: Partial<typeof value>) => void
  errors: Record<string, string>
}) {
  const ids = useId()

  return (
    <div className="space-y-4">
      <p className="text-erp-sm text-muted-foreground border-l-2 border-amber-400 pl-3 py-1">
        Everything on this step is an <strong>allegation</strong> recorded as reported, not
        a finding. Whether the desk believes it is the verification record, set separately
        by somebody checking it against evidence. This whole step can be skipped.
      </p>

      <FieldGrid>
        <Field label="Who is alleged to have done it" htmlFor={`${ids}-perp`}
          error={errors.perpetrator}
          hint="As reported. A description — “a group of about twenty youths” — is a better record than a guessed name.">
          <TextInput id={`${ids}-perp`} max={200} value={value.perpetrator ?? ''}
            onChange={(v) => onChange({ perpetrator: v || null })} />
        </Field>

        <Field label="Their party" htmlFor={`${ids}-pparty`} error={errors.perpetratorPartyId}
          hint="Only where the reporter is sure. Leave empty otherwise.">
          <EntityPicker id={`${ids}-pparty`} source="party"
            placeholder="Search parties…"
            value={value.perpetratorPartyId}
            onChange={(v) => onChange({ perpetratorPartyId: v })} />
        </Field>
      </FieldGrid>

      <FieldGrid>
        <Field label="Related candidate" htmlFor={`${ids}-cand`} error={errors.relatedCandidateId}
          hint="The candidate this incident concerns — not necessarily anyone at fault.">
          <EntityPicker id={`${ids}-cand`} source="candidate"
            placeholder="Search candidates…"
            value={value.relatedCandidateId}
            onChange={(v) => onChange({ relatedCandidateId: v })} />
        </Field>
        <Field label="Related party" htmlFor={`${ids}-rparty`} error={errors.relatedPartyId}
          hint="The party this incident concerns.">
          <EntityPicker id={`${ids}-rparty`} source="party"
            placeholder="Search parties…"
            value={value.relatedPartyId}
            onChange={(v) => onChange({ relatedPartyId: v })} />
        </Field>
      </FieldGrid>

      <div className="pt-2 border-t border-border">
        <div className="text-erp-xs font-medium uppercase tracking-wide text-muted-foreground mb-3">
          Where the claim came from
        </div>
        <FieldGrid>
          <Field label="Source outlet" htmlFor={`${ids}-outlet`} error={errors.sourceOutlet}
            hint="The publication or broadcaster, if this came from one.">
            <TextInput id={`${ids}-outlet`} max={160} value={value.sourceOutlet ?? ''}
              onChange={(v) => onChange({ sourceOutlet: v || null })}
              placeholder="e.g. Daily Nation" />
          </Field>
          <Field label="Source link" htmlFor={`${ids}-url`} error={errors.sourceUrl}>
            <TextInput id={`${ids}-url`} type="url" value={value.sourceUrl ?? ''}
              onChange={(v) => onChange({ sourceUrl: v || null })}
              placeholder="https://…" />
          </Field>
          <Field label="Published at" htmlFor={`${ids}-pub`} error={errors.sourcePublishedAt}>
            <TextInput id={`${ids}-pub`} type="datetime-local"
              value={toLocalInput(value.sourcePublishedAt)}
              onChange={(v) => onChange({ sourcePublishedAt: fromLocalInput(v) })} />
          </Field>
          <Field label="Media coverage" htmlFor={`${ids}-media`} error={errors.mediaCoverageCode}
            hint="How widely this has been reported elsewhere. Defaults to none.">
            <CodeSelect id={`${ids}-media`} codes={vocab?.mediaCoverages}
              value={value.mediaCoverageCode}
              onChange={(v) => onChange({ mediaCoverageCode: v })}
              emptyLabel="None (default)" />
          </Field>
        </FieldGrid>
      </div>
    </div>
  )
}
